import { useMemo } from 'react';

type BaseFacetValue = {
  state: string;
  numberOfResults: number;
};

type StringFacetValue = BaseFacetValue & {
  value: string;
};

type NumericFacetValue = BaseFacetValue & {
  start: number;
  end: number;
  endInclusive: boolean;
};

type DateFacetValue = BaseFacetValue & {
  start: string;
  end: string;
  endInclusive: boolean;
};

type AnyFacetValue = StringFacetValue | NumericFacetValue | DateFacetValue;

function getKey(val: AnyFacetValue): string {
  if ('value' in val) {
    return val.value;
  }
  return `${val.start}-${val.end}`;
}

type FieldStore = {
  seen: Map<string, AnyFacetValue>;
  order: string[];
};

/**
 * Engine-scoped store of previously-seen facet values.
 *
 * Each engine instance gets its own inner map keyed by facet field name.
 * Using a WeakMap ensures entries are garbage collected together with the
 * engine they belong to, so discarding a scoped engine automatically
 * releases its tracked history without requiring manual cleanup.
 *
 * This scoping is required for Phase 3 of the Coveo Headless migration
 * where multiple scoped DPL engines may share facet field names
 * (e.g. `ec_category`) but must not leak seen-value history across
 * engine instances.
 */
const engineScopedStore = new WeakMap<object, Map<string, FieldStore>>();

function getFieldStore(engine: object, field: string): FieldStore {
  let engineStore = engineScopedStore.get(engine);
  if (!engineStore) {
    engineStore = new Map();
    engineScopedStore.set(engine, engineStore);
  }

  let fieldStore = engineStore.get(field);
  if (!fieldStore) {
    fieldStore = { seen: new Map(), order: [] };
    engineStore.set(field, fieldStore);
  }

  return fieldStore;
}

/**
 * Clear all tracked facet value history for a given engine instance.
 *
 * Call this whenever an engine is reset or discarded so the next
 * consumer sees a clean slate. Safe to call with an engine that has
 * never been tracked.
 */
export function clearStableFacetValues(engine: object): void {
  engineScopedStore.delete(engine);
}

/**
 * Preserves all facet values that have ever been returned by Coveo,
 * even if they later disappear from the response (0 results).
 * Values not in the current response are shown with numberOfResults: 0.
 * Maintains the original order values were first seen.
 *
 * The `(engine, field)` pair keys the tracking so that multiple
 * component instances rendering the same facet against the same engine
 * (e.g. sidebar + mobile drawer) share the same history, while
 * different engine instances — including scoped DPL engines that happen
 * to expose the same field name — remain fully isolated from each other.
 */
export function useStableFacetValues<T extends AnyFacetValue>(
  currentValues: T[] | undefined,
  field: string,
  engine: object
): T[] {
  return useMemo(() => {
    if (!currentValues) return [];

    const { seen, order } = getFieldStore(engine, field) as {
      seen: Map<string, T>;
      order: string[];
    };

    // Build a map of current values for quick lookup
    const currentMap = new Map<string, T>();
    for (const val of currentValues) {
      const key = getKey(val);
      currentMap.set(key, val);

      // Track new values in order
      if (!seen.has(key)) {
        order.push(key);
      }
      seen.set(key, val);
    }

    // Return values in original order, using current data if available
    return order.map((key) => {
      const currentVal = currentMap.get(key);
      if (currentVal) {
        return currentVal;
      }
      // Value no longer in response - show with 0 results
      const seenVal = seen.get(key)!;
      return {
        ...seenVal,
        state: 'idle',
        numberOfResults: 0,
      } as T;
    });
  }, [currentValues, field, engine]);
}
